/**
 * Gönderilen mailler (GET /leads/outreach/log, v0.1.169): every outreach mail that went out and every first mail that
 * waits, with where it stands, and the measures of the outreach (Sercan, 11 Oct 2026: "hangi firmaya hangi maili attık,
 * ne zaman attık; gönderilenler ve kalanlar için bir sayfa; taranıp adresi doğrulanan firma sıraya girsin; iyi ölçüm
 * metrikleri olsun"). Read only: nothing on this page sends or approves anything.
 */
import { z } from 'zod';
import { OUTREACH_ADDRESS_STATES, OUTREACH_MAIL_STATES, OUTREACH_REPLY_CLASSES } from './outreach.js';
const count = z.number().int().nonnegative();
const percent = z.number().nonnegative().nullable();
/** Where a waiting first mail stands, in the order the queue shows them. */
export const OUTREACH_QUEUE_STAGES = [
    'scheduled',
    'approved',
    'ready',
    'audit',
    'address',
    'brand_wait',
    'blocked',
];
export const outreachLogMailSchema = z.strictObject({
    messageId: z.string(),
    campaignCode: z.string(),
    company: z.string(),
    domain: z.string().nullable(),
    /** Owner region code (AU-QLD) when known. */
    region: z.string().nullable(),
    country: z.string().nullable(),
    recipient: z.string().nullable(),
    subject: z.string().nullable(),
    /** 1 = first mail, 2 and on = follow-ups. */
    step: count,
    state: z.enum(OUTREACH_MAIL_STATES),
    /** When it went out, was approved or scheduled (ms), null when it has not moved yet. */
    at: z.number().nullable(),
    detail: z.string().nullable(),
    reply: z.strictObject({ classification: z.enum(OUTREACH_REPLY_CLASSES).nullable(), at: z.number() }).nullable(),
    addressState: z.enum(OUTREACH_ADDRESS_STATES),
    /** Waiting first mails only: the queue stage; null for mails that went out. */
    stage: z.enum(OUTREACH_QUEUE_STAGES).nullable(),
    /**
     * v0.1.202: pages opened on cadgaragepro.com from this mail's link after it went out (utm_content=lead-N), null when
     * none. maybeFilter: every visit came within two minutes of the send, the way a recipient's mail filter opens links.
     * humanPages (kontrol listesi B6, 28 Oct 2026): the pages left after the likely scanners (within two minutes of the
     * send, no browser language, HEAD, five or more pages inside ten seconds); 0 means only machines opened the link.
     */
    clicks: z
        .strictObject({
        pages: count,
        humanPages: count,
        firstAt: z.number(),
        lastAt: z.number(),
        maybeFilter: z.boolean(),
    })
        .nullable(),
});
export const outreachLogGroupSchema = z.strictObject({
    key: z.string(),
    sent: count,
    replied: count,
    interested: count,
});
export const outreachLogSchema = z.strictObject({
    asOf: z.number(),
    totals: z.strictObject({
        sent: count,
        /** v0.1.170: every mail of the campaigns' mailboxes in the rolling 24 hours (what the mailbox cap counts). */
        sentToday: count,
        sent7d: count,
        firstMails: count,
        followUps: count,
        bounced: count,
        replied: count,
        interested: count,
        negative: count,
        unsubscribed: count,
        /** Replies from people over delivered mails, percent with one decimal. */
        replyRate: percent,
        bounceRate: percent,
        /** Kontrol listesi B6: firms whose link a person opened, and firms whose link only scanners opened. */
        clickedFirms: count,
        scannerOnlyFirms: count,
        /** Waiting first mails per queue stage. */
        queue: z.strictObject({
            scheduled: count,
            approved: count,
            ready: count,
            audit: count,
            address: count,
            brand_wait: count,
            blocked: count,
        }),
    }),
    /**
     * Mails a day across the campaigns, per mailbox its cap (or its campaigns' limits when lower), for "how long until
     * the queue is through". v0.1.170: the two campaigns share one mailbox, so it is counted once.
     */
    dailyLimit: count,
    /** Last 30 days, oldest first, in the schedule's time zone. */
    daily: z.array(z.strictObject({ day: z.string(), sent: count, replies: count })),
    byRegion: z.array(outreachLogGroupSchema),
    /** By the line that went first for the firm (outreach_plans.arm). */
    byArm: z.array(outreachLogGroupSchema),
    /** Mails that went out, newest first (at most 300). */
    sent: z.array(outreachLogMailSchema),
    /** First mails that wait, in queue order (at most 300). */
    queue: z.array(outreachLogMailSchema),
});
/**
 * One mail's text (GET /leads/outreach/log/{messageId}, v0.1.170; Sercan, 11 Oct 2026: "mail gövdelerini de
 * görebileyim, orijinal dil ve Türkçe olarak"): what went or will go in English, the Turkish Sercan reads, and the
 * latest answer with its Turkish copy when the Temsilci made one. Loaded when a row is opened.
 */
export const outreachLogTextSchema = z.strictObject({
    messageId: z.string(),
    subject: z.string().nullable(),
    body: z.string().nullable(),
    subjectTr: z.string().nullable(),
    bodyTr: z.string().nullable(),
    reply: z
        .strictObject({
        classification: z.enum(OUTREACH_REPLY_CLASSES).nullable(),
        at: z.number(),
        /** The answer as it came; null once retention purged it. */
        text: z.string().nullable(),
        textTr: z.string().nullable(),
    })
        .nullable(),
});
//# sourceMappingURL=outreach-log.js.map